import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, Plus, Trash2, Camera, Video, Receipt, Send, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { uploadFile, useGps, GpsCapture } from "../../lib/field";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { MediaThumb } from "../../components/activity/ActivityCard";
import { COFFEE_ACTIVITY_TYPES, ACTIVITY_TYPES, EXPENSE_CATEGORIES, EVIDENCE, formatRupiah, formatDay, ROLE_HOME, isHarvest, COFFEE_TYPES } from "../../lib/labels";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";

const selectCls = "flex h-12 w-full rounded-md border border-input bg-white px-3 text-base";
const today = () => new Date().toLocaleDateString("en-CA");
const nowTime = () => new Date().toTimeString().slice(0, 5);
const FERT_EXPENSES = [
  { category: "MATERIAL", description: "Pembelian pupuk", amount: "" },
  { category: "LABOR", description: "Tenaga kerja", amount: "" },
  { category: "TRANSPORT", description: "Transport", amount: "" },
  { category: "OTHER", description: "Biaya lain", amount: "" },
];
const EVIDENCE_SLOTS = [
  { cat: "BEFORE", accept: "image/*", icon: Camera }, { cat: "DURING", accept: "image/*", icon: Camera },
  { cat: "AFTER", accept: "image/*", icon: Camera }, { cat: "MATERIAL", accept: "image/*", icon: Camera },
  { cat: "RECEIPT", accept: "image/*,application/pdf", icon: Receipt }, { cat: "GENERAL", accept: "video/*", icon: Video, label: "Video Kegiatan" },
];

const F = ({ label, children, req }) => (
  <div className="space-y-1.5"><Label className="text-sm">{label}{req && <span className="text-red-600"> *</span>}</Label>{children}</div>
);
const Card = ({ title, children }) => (
  <section className="bg-white border border-slate-200/80 rounded-xl p-4 sm:p-6 space-y-4"><h2 className="font-semibold text-slate-900">{title}</h2>{children}</section>
);
const num = (v) => (v === "" || v == null ? null : Number(v));
const PROD_FIELDS = [
  ["harvestDate", "Tanggal panen", "d"], ["cherryWeightKg", "Berat cherry (kg)", "n"],
  ["greenBeanWeightKg", "Berat green bean (kg)", "n"], ["grade", "Grade"], ["moisturePct", "Kadar air (%)", "n"],
  ["defectPct", "Defect (%)", "n"], ["beanSize", "Ukuran biji"], ["cleanliness", "Kebersihan"],
  ["processingMethod", "Metode proses"], ["lotCode", "Kode lot"], ["stockKg", "Stok (kg)", "n"],
];
const PROD_NUM = ["quantity", ...PROD_FIELDS.filter((f) => f[2] === "n").map((f) => f[0])];

const emptyForm = (params) => ({
  projectId: params.get("projectId") || "", activityDate: today(), activityTime: nowTime(),
  activityType: params.get("type") || "", description: "", workResult: "", workers: "", quantityWorked: "", quantityUnit: "ha",
  notes: "", fertilizer: { name: "", type: "", quantity: "", unit: "kg", treesTreated: "", areaTreated: "" },
  production: { quantity: "", unit: "kg", quality: "" }, expenses: params.get("type") === "PEMUPUKAN" ? FERT_EXPENSES : [],
});

const fromActivity = (a) => ({
  ...emptyForm(new URLSearchParams()), ...a, workers: a.workers ?? "", quantityWorked: a.quantityWorked ?? "",
  fertilizer: { ...emptyForm(new URLSearchParams()).fertilizer, ...(a.fertilizer || {}) },
  production: { ...emptyForm(new URLSearchParams()).production, ...(a.production || {}) },
  expenses: (a.expenses || []).map((e) => ({ category: e.category, description: e.description, amount: e.amount })),
});

const ExpenseEditor = ({ rows, setRows }) => {
  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-3">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-end border border-slate-100 rounded-lg p-3" data-testid={`expense-row-${i}`}>
          <div className="col-span-6 sm:col-span-3"><select value={r.category} onChange={(e) => set(i, "category", e.target.value)} className={selectCls} data-testid={`expense-category-${i}`}>{Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <div className="col-span-6 sm:col-span-3"><Input inputMode="numeric" type="number" min="0" placeholder="Jumlah (Rp)" value={r.amount} onChange={(e) => set(i, "amount", e.target.value)} className="h-12 text-base" data-testid={`expense-amount-${i}`} /></div>
          <div className="col-span-10 sm:col-span-5"><Input placeholder="Keterangan" value={r.description} onChange={(e) => set(i, "description", e.target.value)} className="h-12 text-base" data-testid={`expense-desc-${i}`} /></div>
          <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="col-span-2 sm:col-span-1 h-12 grid place-items-center text-red-600" aria-label="Hapus baris" data-testid={`expense-remove-${i}`}><Trash2 className="h-5 w-5" /></button>
        </div>
      ))}
      <Button type="button" variant="outline" className="h-12 w-full" onClick={() => setRows([...rows, { category: "OTHER", description: "", amount: "" }])} data-testid="expense-add-btn"><Plus className="h-4 w-4 mr-1.5" /> Tambah Baris Biaya</Button>
      <p className="flex justify-between font-bold text-slate-900 border-t pt-3" data-testid="expense-total">TOTAL <span>{formatRupiah(total)}</span></p>
    </div>
  );
};

const EvidencePicker = ({ pending, setPending, existing, onRemoveExisting }) => {
  const refs = useRef({});
  const add = (cat, files) => {
    const list = Array.from(files || []);
    setPending((p) => [...p, ...list
      .filter((file) => !p.some((x) => x.category === cat && x.file.name === file.name && x.file.size === file.size))
      .map((file) => ({ key: `${Date.now()}-${Math.random()}`, file, category: cat, progress: 0, state: "waiting" }))]);
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {EVIDENCE_SLOTS.map((s) => (
          <div key={s.cat + s.accept}>
            <input ref={(el) => { refs.current[s.cat + s.accept] = el; }} type="file" accept={s.accept} multiple className="hidden"
              onChange={(e) => { add(s.cat, e.target.files); e.target.value = ""; }} data-testid={`evidence-input-${s.cat}${s.accept.startsWith("video") ? "-video" : ""}`} />
            <button type="button" onClick={() => refs.current[s.cat + s.accept]?.click()} className="w-full min-h-[56px] rounded-lg border-2 border-dashed border-emerald-800/30 bg-emerald-50/50 px-2 py-2 text-sm font-semibold text-emerald-900 flex items-center justify-center gap-1.5" data-testid={`evidence-btn-${s.cat}${s.accept.startsWith("video") ? "-video" : ""}`}>
              <s.icon className="h-4 w-4 shrink-0" /> {s.label || EVIDENCE[s.cat]}
            </button>
          </div>
        ))}
      </div>
      {(existing.length > 0 || pending.length > 0) && (
        <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="evidence-list">
          {existing.map((m) => (
            <li key={m.id} className="relative rounded-lg overflow-hidden border bg-slate-50">
              <div className="aspect-square"><MediaThumb m={m} /></div>
              <p className="text-[11px] px-2 py-1 truncate">{m.caption || EVIDENCE[m.category]}</p>
              <button type="button" onClick={() => onRemoveExisting(m)} className="absolute top-1 right-1 h-7 w-7 rounded-full bg-white/90 grid place-items-center text-red-600" aria-label="Hapus" data-testid={`existing-remove-${m.id}`}><X className="h-4 w-4" /></button>
            </li>
          ))}
          {pending.map((p) => (
            <li key={p.key} className="relative rounded-lg border bg-white p-2 text-xs" data-testid="pending-file">
              <p className="font-semibold truncate">{p.file.name}</p>
              <p className="text-slate-500">{EVIDENCE[p.category]} · {(p.file.size / 1048576).toFixed(1)} MB</p>
              {p.state !== "done" && <input value={p.caption || ""} maxLength={300} onChange={(e) => setPending((l) => l.map((x) => (x.key === p.key ? { ...x, caption: e.target.value } : x)))} placeholder="Catatan foto (opsional)" className="mt-1.5 w-full h-9 rounded border px-2 text-xs" data-testid="pending-caption-input" />}
              <div className="mt-2 h-1.5 rounded bg-slate-100 overflow-hidden"><div className={`h-full ${p.state === "error" ? "bg-red-500" : "bg-emerald-600"}`} style={{ width: `${p.state === "done" ? 100 : p.progress}%` }} /></div>
              <p className="mt-1">{{ waiting: "Siap diunggah", uploading: `Mengunggah ${p.progress}%`, done: "Terunggah", error: p.error }[p.state]}</p>
              {p.state === "error" && <button type="button" onClick={() => setPending((l) => l.map((x) => (x.key === p.key ? { ...x, state: "waiting", progress: 0 } : x)))} className="mt-1 text-emerald-800 font-semibold underline" data-testid="pending-retry-btn">Coba lagi</button>}
              {p.state !== "done" && p.state !== "uploading" && <button type="button" onClick={() => setPending((l) => l.filter((x) => x.key !== p.key))} className="absolute top-1 right-1 text-red-600" aria-label="Batal"><X className="h-4 w-4" /></button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const buildPayload = (form, gps, correctionOf) => ({
  projectId: form.projectId, activityDate: form.activityDate, activityTime: form.activityTime, activityType: form.activityType,
  description: form.description, workResult: form.workResult, workers: num(form.workers), quantityWorked: num(form.quantityWorked),
  quantityUnit: form.quantityUnit, notes: form.notes, correctionOf: correctionOf || null,
  gps: gps ? { latitude: gps.latitude, longitude: gps.longitude, accuracy: gps.accuracy ?? null, capturedAt: gps.capturedAt } : null,
  fertilizer: form.activityType === "PEMUPUKAN" ? { ...form.fertilizer, quantity: num(form.fertilizer.quantity), treesTreated: num(form.fertilizer.treesTreated), areaTreated: num(form.fertilizer.areaTreated) } : null,
  production: isHarvest(form.activityType) ? Object.fromEntries(Object.entries(form.production).map(([k, v]) => [k, PROD_NUM.includes(k) ? num(v) : v])) : null,
  expenses: form.expenses.filter((e) => Number(e.amount) > 0).map((e) => ({ category: e.category, description: e.description || EXPENSE_CATEGORIES[e.category], amount: Math.round(Number(e.amount)) })),
});

export default function ActivityForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const base = ROLE_HOME[user.role];
  const correctionOf = params.get("correctionOf");
  const [form, setForm] = useState(() => emptyForm(params));
  const [activityId, setActivityId] = useState(id || null);
  const [projects, setProjects] = useState([]);
  const [existing, setExisting] = useState([]);
  const [savedGps, setSavedGps] = useState(null);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [gpsState, captureGps] = useGps();
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    api.get("/me/projects").then((r) => setProjects(r.data)).catch(() => {});
    const src = id || correctionOf;
    if (src) {
      api.get(`/activities/${src}`).then(({ data }) => {
        setForm(fromActivity(data));
        if (id) { setExisting(data.media || []); setSavedGps(data.gps || null); }
      }).catch((e) => toast.error(formatApiError(e)));
    }
  }, [id, correctionOf]);

  const project = useMemo(() => projects.find((p) => p.id === form.projectId), [projects, form.projectId]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (grp, k, v) => setForm((f) => ({ ...f, [grp]: { ...f[grp], [k]: v } }));
  const onType = (t) => setForm((f) => ({ ...f, activityType: t, expenses: t === "PEMUPUKAN" && f.expenses.length === 0 ? FERT_EXPENSES : f.expenses }));

  const removeExisting = async (m) => {
    try {
      await api.delete(`/activities/${activityId}/media/${m.id}`);
      setExisting((l) => l.filter((x) => x.id !== m.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadAll = async (aid) => {
    for (const p of pending.filter((x) => x.state !== "done")) {
      const update = (patch) => setPending((l) => l.map((x) => (x.key === p.key ? { ...x, ...patch } : x)));
      update({ state: "uploading", progress: 0 });
      try {
        const { data } = await uploadFile(`/activities/${aid}/media`, p.file, { category: p.category, caption: p.caption || "" }, (progress) => update({ progress }));
        update({ state: "done" });
        setExisting((l) => [...l, data]);
      } catch (e) {
        update({ state: "error", error: formatApiError(e) });
        throw e;
      }
    }
    setPending((l) => l.filter((x) => x.state !== "done"));
  };

  const counts = useMemo(() => {
    const all = [...existing.map((m) => ({ cat: m.category, type: m.type })),
      ...pending.map((p) => ({ cat: p.category, type: p.file.type.startsWith("video") ? "VIDEO" : "PHOTO" }))];
    return { photos: all.filter((x) => x.type === "PHOTO" && x.cat !== "RECEIPT").length, videos: all.filter((x) => x.type === "VIDEO").length, receipts: all.filter((x) => x.cat === "RECEIPT").length };
  }, [existing, pending]);
  const total = form.expenses.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const openConfirm = () => {
    if (!form.projectId || !form.activityType || form.description.trim().length < 3) return toast.error("Lengkapi kebun, jenis kegiatan, dan uraian.");
    if (project && project.projectType === "PRIVATE_MANAGED_COFFEE_PROJECT" && !project.ownerName) return toast.error("Belum ada pemilik lahan yang terhubung dengan kebun ini. Simpan sebagai draf.");
    if (project && project.projectType !== "PRIVATE_MANAGED_COFFEE_PROJECT" && !project.investorNames?.length) return toast.error("Belum ada investor yang terhubung dengan kebun ini. Simpan sebagai draf.");
    setConfirm(true);
  };

  const save = async (submit) => {
    if (!form.projectId || !form.activityType || form.description.trim().length < 3) return toast.error("Lengkapi kebun, jenis kegiatan, dan uraian.");
    setBusy(true);
    let aid = activityId;
    try {
      const payload = buildPayload(form, gpsState.gps || savedGps, aid ? null : correctionOf);
      if (aid) await api.patch(`/activities/${aid}`, payload);
      else { aid = (await api.post("/activities", payload)).data.id; setActivityId(aid); }
      await uploadAll(aid);
      if (submit) await api.post(`/activities/${aid}/submit`);
      toast.success(submit ? "Kegiatan dikirim ke PT SJI" : "Draf disimpan");
      navigate(`${base}/kegiatan/${aid}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
      if (aid && !id) navigate(`${base}/kegiatan/${aid}/ubah`, { replace: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5 pb-28" data-testid="activity-form-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">{id ? "Ubah Kegiatan" : correctionOf ? "Koreksi Kegiatan" : "Input Kegiatan Lapangan"}</h1>
        {correctionOf && <p className="mt-1.5 text-sm text-amber-800">Data asli tetap tersimpan. Koreksi ini dibuat oleh kelompok tani / koperasi dan dipantau PT SJI.</p>}
      </div>
      <Card title="1. Kebun & Waktu">
        <F label="Kebun" req>
          <select value={form.projectId} onChange={(e) => set("projectId", e.target.value)} disabled={!!id || !!correctionOf || !!params.get("projectId")} className={selectCls} data-testid="activity-project-select">
            <option value="">- Pilih kebun -</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.projectCode} - {p.name}</option>)}
          </select>
        </F>
        {project && <ProjectContext p={project} />}
        <div className="grid grid-cols-2 gap-3">
          <F label="Tanggal" req><Input type="date" value={form.activityDate} onChange={(e) => set("activityDate", e.target.value)} className="h-12 text-base" data-testid="activity-date-input" /></F>
          <F label="Jam" req><Input type="time" value={form.activityTime} onChange={(e) => set("activityTime", e.target.value)} className="h-12 text-base" data-testid="activity-time-input" /></F>
        </div>
        <F label="Jenis Kegiatan" req>
          <select value={form.activityType} onChange={(e) => onType(e.target.value)} className={selectCls} data-testid="activity-type-select">
            <option value="">- Pilih jenis -</option>
            {Object.entries({ ...COFFEE_ACTIVITY_TYPES, ...(COFFEE_ACTIVITY_TYPES[form.activityType] || !form.activityType ? {} : { [form.activityType]: ACTIVITY_TYPES[form.activityType] }) }).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </F>
      </Card>
      <Card title="2. Uraian Pekerjaan">
        <F label="Uraian kegiatan" req><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="text-base" data-testid="activity-description-input" /></F>
        <F label="Hasil kerja"><Textarea rows={2} value={form.workResult} onChange={(e) => set("workResult", e.target.value)} className="text-base" data-testid="activity-result-input" /></F>
        <div className="grid grid-cols-3 gap-3">
          <F label="Jumlah pekerja"><Input type="number" inputMode="numeric" min="0" value={form.workers} onChange={(e) => set("workers", e.target.value)} className="h-12 text-base" data-testid="activity-workers-input" /></F>
          <F label="Luas/Jumlah"><Input type="number" inputMode="decimal" min="0" step="any" value={form.quantityWorked} onChange={(e) => set("quantityWorked", e.target.value)} className="h-12 text-base" data-testid="activity-quantity-input" /></F>
          <F label="Satuan"><Input value={form.quantityUnit} onChange={(e) => set("quantityUnit", e.target.value)} className="h-12 text-base" data-testid="activity-unit-input" /></F>
        </div>
      </Card>
      {form.activityType === "PEMUPUKAN" && (
        <Card title="3. Detail Pemupukan">
          <div className="grid grid-cols-2 gap-3">
            <F label="Nama pupuk"><Input value={form.fertilizer.name} onChange={(e) => setNested("fertilizer", "name", e.target.value)} className="h-12 text-base" data-testid="fert-name-input" /></F>
            <F label="Jenis pupuk"><Input value={form.fertilizer.type} onChange={(e) => setNested("fertilizer", "type", e.target.value)} placeholder="Organik / Kimia" className="h-12 text-base" data-testid="fert-type-input" /></F>
            <F label="Jumlah dipakai"><Input type="number" inputMode="decimal" step="any" min="0" value={form.fertilizer.quantity} onChange={(e) => setNested("fertilizer", "quantity", e.target.value)} className="h-12 text-base" data-testid="fert-qty-input" /></F>
            <F label="Satuan"><Input value={form.fertilizer.unit} onChange={(e) => setNested("fertilizer", "unit", e.target.value)} className="h-12 text-base" data-testid="fert-unit-input" /></F>
            <F label="Jumlah pohon"><Input type="number" inputMode="numeric" min="0" value={form.fertilizer.treesTreated} onChange={(e) => setNested("fertilizer", "treesTreated", e.target.value)} className="h-12 text-base" data-testid="fert-trees-input" /></F>
            <F label="Luas dipupuk (ha)"><Input type="number" inputMode="decimal" step="any" min="0" value={form.fertilizer.areaTreated} onChange={(e) => setNested("fertilizer", "areaTreated", e.target.value)} className="h-12 text-base" data-testid="fert-area-input" /></F>
          </div>
          <p className="text-xs text-amber-800">Bukti wajib saat kirim: foto sebelum, foto sesudah, dan nota bila ada biaya. Disarankan juga foto saat kegiatan dan foto pupuk.</p>
        </Card>
      )}
      {isHarvest(form.activityType) && (
        <Card title="3. Data Produksi Kopi">
          <div className="grid grid-cols-2 gap-3">
            {PROD_FIELDS.map(([k, label, type]) => (
              <F key={k} label={label}><Input type={type === "n" ? "number" : type === "d" ? "date" : "text"} inputMode={type === "n" ? "decimal" : undefined} step="any" min="0" value={form.production[k] ?? ""} onChange={(e) => setNested("production", k, e.target.value)} className="h-12 text-base" data-testid={`prod-${k}-input`} /></F>
            ))}
          </div>
          {num(form.production.cherryWeightKg) > 0 && num(form.production.greenBeanWeightKg) > 0 && (
            <p className="text-sm text-emerald-800" data-testid="prod-rendemen">Rendemen: {((num(form.production.greenBeanWeightKg) / num(form.production.cherryWeightKg)) * 100).toFixed(1)}%</p>
          )}
        </Card>
      )}
      <Card title="Biaya Kegiatan"><ExpenseEditor rows={form.expenses} setRows={(rows) => set("expenses", rows)} /></Card>
      <Card title="Lokasi GPS"><GpsCapture state={gpsState} capture={captureGps} current={savedGps} /></Card>
      <Card title="Foto, Video & Bukti">
        <EvidencePicker pending={pending} setPending={setPending} existing={existing} onRemoveExisting={removeExisting} />
        <p className="text-xs text-slate-500">Foto/PDF maks 15 MB, video maks 100 MB per file.</p>
      </Card>
      <Card title="Catatan"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="text-base" data-testid="activity-notes-input" /></Card>
      <div className="fixed bottom-0 inset-x-0 lg:left-64 z-30 bg-white/95 backdrop-blur border-t border-slate-200 p-3">
        <div className="max-w-3xl mx-auto grid grid-cols-2 gap-3">
          <Button type="button" variant="outline" disabled={busy} onClick={() => save(false)} className="h-12 text-base" data-testid="activity-save-draft-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Simpan Draf</Button>
          <Button type="button" disabled={busy} onClick={openConfirm} className="h-12 px-2 text-xs sm:text-base whitespace-normal leading-tight bg-emerald-800 hover:bg-emerald-900 font-bold" data-testid="activity-submit-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 shrink-0 animate-spin" /> : <Send className="h-4 w-4 mr-1.5 shrink-0" />} KIRIM LAPORAN KEGIATAN</Button>
        </div>
        <p className="max-w-3xl mx-auto mt-1.5 text-center text-[11px] text-slate-500">Laporan akan dapat dilihat oleh {project?.projectType === "PRIVATE_MANAGED_COFFEE_PROJECT" ? "Pemilik Lahan" : "Investor terkait"} dan PT SJI.</p>
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="confirm-dialog">
          <DialogHeader><DialogTitle>LAPORAN AKAN DIKIRIM</DialogTitle></DialogHeader>
          <dl className="text-sm grid grid-cols-2 gap-y-2 gap-x-3" data-testid="confirm-summary">
            <dt className="text-slate-500">Lahan</dt><dd className="font-mono font-semibold">{project?.land?.landCode} · {project?.projectCode}</dd>
            <dt className="text-slate-500">{project?.projectType === "PRIVATE_MANAGED_COFFEE_PROJECT" ? "Pemilik Lahan" : "Investor"}</dt><dd className="font-semibold" data-testid="confirm-investor">{project?.projectType === "PRIVATE_MANAGED_COFFEE_PROJECT" ? project?.ownerName : project?.investorNames?.join(", ")}</dd>
            <dt className="text-slate-500">Kegiatan</dt><dd>{ACTIVITY_TYPES[form.activityType]}</dd>
            <dt className="text-slate-500">Tanggal</dt><dd>{formatDay(form.activityDate)} {form.activityTime}</dd>
            <dt className="text-slate-500">Total Biaya</dt><dd className="font-bold" data-testid="confirm-total">{formatRupiah(total)}</dd>
            <dt className="text-slate-500">Foto</dt><dd data-testid="confirm-photos">{counts.photos}</dd>
            <dt className="text-slate-500">Video</dt><dd data-testid="confirm-videos">{counts.videos}</dd>
            <dt className="text-slate-500">Nota</dt><dd data-testid="confirm-receipts">{counts.receipts}</dd>
            <dt className="text-slate-500">GPS</dt><dd data-testid="confirm-gps">{gpsState.gps || savedGps ? "Terekam" : "Tidak ada"}</dd>
            <dt className="text-slate-500">Dapat dilihat oleh</dt><dd className="font-semibold">{project?.projectType === "PRIVATE_MANAGED_COFFEE_PROJECT" ? "Pemilik Lahan" : "Investor terkait"} + PT SJI</dd>
          </dl>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Button variant="outline" className="h-12" onClick={() => setConfirm(false)} data-testid="confirm-back-btn">KEMBALI</Button>
            <Button className="h-12 bg-emerald-800 hover:bg-emerald-900 font-bold" disabled={busy} onClick={() => { setConfirm(false); save(true); }} data-testid="confirm-send-btn">KIRIM LAPORAN</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ProjectContext = ({ p }) => (
  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm space-y-1" data-testid="activity-land-info">
    <p><span className="text-slate-500">Kebun: </span><span className="font-mono font-semibold">{p.projectCode}</span> · {p.name}</p>
    <p><span className="text-slate-500">Lahan: </span><span className="font-mono">{p.land?.landCode}</span> · {p.land?.name} · {p.land?.areaHectares} ha</p>
    <p><span className="text-slate-500">Jenis kopi: </span>{COFFEE_TYPES[p.coffeeType] || p.commodity || "-"} · <span className="text-slate-500">Kelompok: </span>{p.farmerGroupName}</p>
    {p.projectType === "PRIVATE_MANAGED_COFFEE_PROJECT" ? <>
      <p><span className="text-slate-500">Pemilik lahan: </span><span className="font-semibold" data-testid="activity-owner-readonly">{p.ownerName || "Belum ada pemilik lahan yang terhubung."}</span></p>
      <p className="text-xs text-emerald-900 font-semibold">Laporan kegiatan ini akan dapat dilihat oleh Pemilik Lahan dan PT SJI.</p>
    </> : <>
    <p><span className="text-slate-500">Investor terkait: </span><span className="font-semibold" data-testid="activity-investor-readonly">{p.investorNames?.join(", ") || "Belum ada investor yang terhubung dengan kebun ini."}</span></p>
    <p className="text-xs text-emerald-900 font-semibold">Laporan kegiatan ini akan dapat dilihat oleh Investor terkait dan PT SJI.</p>
    </>}
  </div>
);
